// src/data/rooms.ts
import type { Language } from "@/lib/i18n";

interface RoomPrices {
  daily: { value: string; unit: string };
  monthly: { value: string; unit: string };
}

interface RoomSpecs {
  size: string;
  capacity: string;
  bed: string;
}

interface RoomDescription {
  short: string;
  long: string;
}

interface RoomFeatures {
  summary: string[];
  amenities: string[];
}

interface Room {
  id: number;
  name: string;
  slug: string;
  isBestValue: boolean;
  prices: RoomPrices;
  images: { main: string };
  specs: RoomSpecs;
  description: RoomDescription;
  features: RoomFeatures;
}

const roomsDataByLang: Record<Language, Room[]> = {
  id: [
    {
      id: 1,
      name: "Studio Deluxe",
      slug: "studio-deluxe",
      isBestValue: false,
      prices: {
        daily: { value: "850.000", unit: "malam" },
        monthly: { value: "9.500.000", unit: "bulan" },
      },
      images: {
        main: "/images/rooms/room-type-1.webp",
      },
      specs: {
        size: "28 m²",
        capacity: "1-2 Orang",
        bed: "King Size Bed (180x200)",
      },
      description: {
        short:
          "Solusi hunian cerdas untuk profesional muda yang mengutamakan efisiensi tanpa kompromi kenyamanan.",
        long: "Studio Deluxe menghadirkan konsep living space yang sempurna untuk profesional modern. Dirancang dengan interior minimalis bergaya Bali kontemporer, work station ergonomis, dan pencahayaan natural yang menenangkan.",
      },
      features: {
        summary: [
          'Smart TV 43"',
          "King Bed",
          "Meja Ergonomis",
          "Kamar Mandi Dalam",
        ],
        amenities: [
          "Wi-Fi Berkecepatan Tinggi Premium",
          "Smart TV 43 inci dengan Netflix",
          "Kamar Mandi Dalam Premium",
          "Meja Kerja Ergonomis dengan USB Port",
          "AC Daikin Inverter",
          "Akses ke Fasilitas KinKen Club",
          "Layanan Concierge 24/7",
          "Housekeeping Mingguan",
        ],
      },
    },
    {
      id: 2,
      name: "Executive Loft",
      slug: "executive-loft",
      isBestValue: true,
      prices: {
        daily: { value: "1.250.000", unit: "malam" },
        monthly: { value: "12.500.000", unit: "bulan" },
      },
      images: {
        main: "/images/rooms/room-type-2.webp",
      },
      specs: {
        size: "42 m²",
        capacity: "Maks 2 Orang",
        bed: "Super King Bed (200x200)",
      },
      description: {
        short:
          "Favorit ekspatriat dan profesional eksekutif dengan ruang lebih luas dan balkon pribadi menghadap taman.",
        long: "Executive Loft menawarkan pengalaman living yang lebih eksklusif dengan konsep two-level living. Dilengkapi balkon pribadi untuk menikmati udara segar Bali dan kitchenette mini untuk kebutuhan sehari-hari.",
      },
      features: {
        summary: [
          "Balkon Pribadi",
          "Super King Bed",
          "Kitchenette",
          "Work Lounge",
        ],
        amenities: [
          "Semua fasilitas Studio Deluxe",
          "Balkon Pribadi dengan Pemandangan Taman",
          "Kitchenette dengan Kulkas Mini",
          "Sofa Lounge Premium 2 Seater",
          "Walk-in Closet dengan Full Mirror",
          "Layanan Housekeeping Harian",
          "Akses Concierge Prioritas",
          "Laundry Gratis Mingguan (15kg)",
        ],
      },
    },
    {
      id: 3,
      name: "Penthouse Signature",
      slug: "penthouse-signature",
      isBestValue: false,
      prices: {
        daily: { value: "2.500.000", unit: "malam" },
        monthly: { value: "22.000.000", unit: "bulan" },
      },
      images: {
        main: "/images/rooms/room-type-3.webp",
      },
      specs: {
        size: "65 m²",
        capacity: "Maks 2 Orang",
        bed: "Emperor Bed (220x220)",
      },
      description: {
        short:
          "Puncak kemewahan KinKen dengan living area terpisah, bathtub freestanding, dan layanan butler pribadi.",
        long: "The ultimate expression of luxury living. Penthouse Signature menempati lantai teratas dengan panorama Denpasar Selatan. Menghadirkan privasi absolut, bathroom dengan bathtub freestanding, dan layanan VIP untuk pengalaman tak tertandingi.",
      },
      features: {
        summary: [
          "Teras Pribadi",
          "Emperor Bed",
          "Bathtub Freestanding",
          "VIP Butler",
        ],
        amenities: [
          "Semua fasilitas Executive Loft",
          "Teras Pribadi dengan Outdoor Seating",
          "Bathtub Freestanding dengan Chromatherapy",
          "Dapur Lengkap",
          "Smart TV 65 inci 4K UHD",
          "Layanan VIP Butler 24/7",
          "Transfer Bandara Premium",
          "Layanan Laundry Unlimited",
          "Akses Eksklusif ke Rooftop Lounge",
          "Welcome Amenities Personal",
        ],
      },
    },
  ],
  en: [
    {
      id: 1,
      name: "Studio Deluxe",
      slug: "studio-deluxe",
      isBestValue: false,
      prices: {
        daily: { value: "850,000", unit: "night" },
        monthly: { value: "9,500,000", unit: "month" },
      },
      images: {
        main: "/images/rooms/room-type-1.webp",
      },
      specs: {
        size: "28 m²",
        capacity: "1-2 Guests",
        bed: "King Size Bed (180x200)",
      },
      description: {
        short:
          "Smart living solution for young professionals who prioritize efficiency without compromising comfort.",
        long: "Studio Deluxe presents the perfect living space concept for modern professionals. Designed with minimalist contemporary Balinese interiors, ergonomic workstation, and calming natural lighting.",
      },
      features: {
        summary: [
          'Smart TV 43"',
          "King Bed",
          "Ergonomic Desk",
          "En-suite Bath",
        ],
        amenities: [
          "High-Speed Premium Wi-Fi",
          "43-inch Smart TV with Netflix",
          "Premium En-suite Bathroom",
          "Ergonomic Work Desk with USB Port",
          "Daikin Inverter AC",
          "Access to KinKen Club Facilities",
          "24/7 Concierge Service",
          "Weekly Housekeeping",
        ],
      },
    },
    {
      id: 2,
      name: "Executive Loft",
      slug: "executive-loft",
      isBestValue: true,
      prices: {
        daily: { value: "1,250,000", unit: "night" },
        monthly: { value: "12,500,000", unit: "month" },
      },
      images: {
        main: "/images/rooms/room-type-2.webp",
      },
      specs: {
        size: "42 m²",
        capacity: "Max 2 Guests",
        bed: "Super King Bed (200x200)",
      },
      description: {
        short:
          "Favorite of expatriates and executive professionals with larger space and private balcony overlooking the garden.",
        long: "Executive Loft offers a more exclusive living experience with two-level living concept. Equipped with private balcony to enjoy Bali's fresh air and mini kitchenette for daily needs.",
      },
      features: {
        summary: [
          "Private Balcony",
          "Super King Bed",
          "Kitchenette",
          "Work Lounge",
        ],
        amenities: [
          "All Studio Deluxe amenities",
          "Private Balcony with Garden View",
          "Kitchenette with Mini Fridge",
          "Premium 2-Seater Lounge Sofa",
          "Walk-in Closet with Full Mirror",
          "Daily Housekeeping Service",
          "Priority Concierge Access",
          "Free Weekly Laundry (15kg)",
        ],
      },
    },
    {
      id: 3,
      name: "Penthouse Signature",
      slug: "penthouse-signature",
      isBestValue: false,
      prices: {
        daily: { value: "2,500,000", unit: "night" },
        monthly: { value: "22,000,000", unit: "month" },
      },
      images: {
        main: "/images/rooms/room-type-3.webp",
      },
      specs: {
        size: "65 m²",
        capacity: "Max 2 Guests",
        bed: "Emperor Bed (220x220)",
      },
      description: {
        short:
          "The pinnacle of KinKen luxury with separate living area, freestanding bathtub, and private butler service.",
        long: "The ultimate expression of luxury living. Penthouse Signature occupies the top floor with South Denpasar panorama. Offering absolute privacy, bathroom with freestanding bathtub, and VIP service for an unmatched experience.",
      },
      features: {
        summary: [
          "Private Terrace",
          "Emperor Bed",
          "Freestanding Bathtub",
          "VIP Butler",
        ],
        amenities: [
          "All Executive Loft amenities",
          "Private Terrace with Outdoor Seating",
          "Freestanding Bathtub with Chromatherapy",
          "Fully Equipped Kitchen",
          "65-inch 4K UHD Smart TV",
          "24/7 VIP Butler Service",
          "Premium Airport Transfer",
          "Unlimited Laundry Service",
          "Exclusive Access to Rooftop Lounge",
          "Personalized Welcome Amenities",
        ],
      },
    },
  ],
};

// Backward compatibility
export const ROOMS_DATA = roomsDataByLang.id;

// i18n function
export function getRoomsData(lang: Language = "id"): Room[] {
  return roomsDataByLang[lang] || roomsDataByLang.id;
}
